import { Period } from '../../../../gql/social/graphql';

const validPeriods = ['ALL', 'DAY', 'MONTH', 'QUARTER', 'WEEK', 'YEAR'] as const;

export const isValidPeriod = (value: string): value is Period => validPeriods.includes(value as Period);

export const toPeriod = (value: string): Period | null => {
  if (isValidPeriod(value)) {
    return value;
  }
  return null;
};

/**
 * Determines the appropriate precision based on the date range length
 * @param startDate - Start date in YYYY-MM-DD format
 * @param endDate - End date in YYYY-MM-DD format
 * @returns Period - The appropriate precision for the GraphQL query
 * @throws Error if the provided date strings are invalid
 */
export const calculatePrecisionFromDateRange = (startDate: string, endDate: string): Period => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  // Validate that both dates are valid
  if (isNaN(start.getTime())) {
    throw new Error(`Invalid start date provided: "${startDate}". Expected format: YYYY-MM-DD`);
  }

  if (isNaN(end.getTime())) {
    throw new Error(`Invalid end date provided: "${endDate}". Expected format: YYYY-MM-DD`);
  }

  // Validate that start date is not after end date
  if (start > end) {
    throw new Error(`Start date "${startDate}" cannot be after end date "${endDate}"`);
  }

  // Calculate precise months difference using calendar arithmetic
  const yearDiff = end.getFullYear() - start.getFullYear();
  const monthDiff = end.getMonth() - start.getMonth();
  const totalMonthsDiff = yearDiff * 12 + monthDiff;

  // Add partial month if end day is greater than start day
  const diffMonths = totalMonthsDiff + (end.getDate() >= start.getDate() ? 0 : -1) + 1;

  // Calculate precise quarters difference
  const startQuarter = Math.floor(start.getMonth() / 3);
  const endQuarter = Math.floor(end.getMonth() / 3);
  const diffQuarters = (end.getFullYear() - start.getFullYear()) * 4 + (endQuarter - startQuarter) + 1;

  // Determine precision based on range length
  if (diffQuarters > 3) {
    // More than 3 quarters (9+ months) -> use QUARTER
    return 'QUARTER';
  } else if (diffMonths > 2) {
    // More than 2 months but less than 9 months -> use MONTH
    return 'MONTH';
  } else {
    // 2 months or less -> use DAY
    return 'DAY';
  }
};
